// Product Card Dropdown Handler - Scoped for individual product cards
class ProductCardDropdown {
  constructor(productCard) {
    this.productCard = productCard;
    this.dropdown = productCard.querySelector('.product-form__input--dropdown');
    this.dropdownContent = this.dropdown ? this.dropdown.querySelector('.product-form-inline_dropdown__content') : null;
    this.optionsContainer = this.dropdown ? this.dropdown.querySelector('.product-form-inline_dropdown__options') : null;
    this.selectedContainer = this.dropdown ? this.dropdown.querySelector('.product-form-inline_dropdown__selected') : null;
    this.inlineForm = productCard.querySelector('.product-form-inline-form');
    this.productTabs = this.dropdown ? this.dropdown.querySelectorAll('.product-tab') : [];
    
    // Add flag to track if Rebuy listener has been added
    this.rebuyListenerAdded = false;
    
    // Price elements
    this.priceWrapper = productCard.querySelector('.price-info-wpr');
    this.priceContainer = this.priceWrapper ? this.priceWrapper.querySelector('.price__container') : null;
    
    // If not found, try alternative selectors
    if (!this.priceContainer) {
      this.priceContainer = productCard.querySelector('.price__container');
    }
    
    this.salePriceContainer = this.priceContainer ? this.priceContainer.querySelector('.price__sale') : null;
    this.regularPriceContainer = this.priceContainer ? this.priceContainer.querySelector('.price__regular') : null;
    
    
    // Also include one-time-purchase tabs that might be outside the dropdown
    const otpTabs = this.productCard.querySelectorAll('.one-time-purchase.product-tab');
    this.productTabs = [...this.productTabs, ...otpTabs];
    
    if (this.dropdown && this.dropdownContent && this.optionsContainer) {
      this.init();
      this.initializeDefaultPrice();
    }
    
    // Setup Rebuy prevention on form submission (once on initialization)
    if (this.inlineForm) {
      this.inlineForm.addEventListener('submit', () => {
        const sellingPlan = this.inlineForm.querySelector('input[name="selling_plan"]')?.value;
        console.log('Form submitted, destroying Rebuy SmartCart if it exists');
        console.log('Selling plan:', sellingPlan);
        // Only destroy Rebuy SmartCart if a selling plan is selected
        
        if (window.Rebuy && window.Rebuy.SmartCart && sellingPlan) {
          window.Rebuy.SmartCart.destroy();
        }
      });
    }
    
    // Handle window resize to recalculate dropdown position
    this.handleResize = this.handleResize.bind(this);
    window.addEventListener('resize', this.handleResize);
  }
  
  init() {
    // Toggle dropdown on click
    this.dropdownContent.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      
      // Close other dropdowns
      this.closeOtherDropdowns();
      
      // Toggle current dropdown
      const isOpen = this.dropdown.classList.contains('is-open');
      if (!isOpen) {
        this.dropdown.classList.add('is-open');
        this.optionsContainer.style.display = 'block';
        
        // Check if dropdown should open upward
        this.adjustDropdownPosition();
      } else {
        this.closeDropdown();
      }
    });
    
    // Handle option selection
    this.productTabs.forEach((tab) => {
      tab.addEventListener('click', (e) => {
        // For one-time-purchase tabs, don't prevent default if it contains a button or link
        const isOtpTab = tab.classList.contains('one-time-purchase');
        const hasButton = tab.querySelector('button, a');
        
        if (!isOtpTab || !hasButton) {
          e.preventDefault();
          e.stopPropagation();
        }
        
        // Update selected state for all tabs (including OTP)
        this.productTabs.forEach(t => t.classList.remove('selected'));
        tab.classList.add('selected');
        
        // Get variant data
        const options = JSON.parse(tab.dataset.options || '{}');
        
        // Update the dropdown display for all tabs
        if (this.selectedContainer) {
          const contentWpr = tab.querySelector('.content-wpr');
          if (contentWpr) {
            // Regular tab with content-wpr
            this.selectedContainer.innerHTML = contentWpr.outerHTML;
          } else {
            // One-time-purchase tab - use the button or link content
            const otpButton = tab.querySelector('.one-time-purchase--button');
            const otpLink = tab.querySelector('.klaviyo-bis-trigger');
            
            if (otpButton) {
              this.selectedContainer.innerHTML = `<div class="content-wpr">${otpButton.outerHTML}</div>`;
            } else if (otpLink) {
              this.selectedContainer.innerHTML = `<div class="content-wpr">${otpLink.outerHTML}</div>`;
            }
          }
        }
        
        // Update form inputs for both regular and OTP tabs
        this.updateFormInputs(options);
        
        // Update product card pricing
        this.updateProductCardPrice(options);
        
        // Close dropdown for all product tabs
        this.closeDropdown();
      });
    });
  }
  
  updateFormInputs(options) {
    if (this.inlineForm && options.product_id) {
      const variantInput = this.inlineForm.querySelector('input[name="id"]');
      let sellingPlanInput = this.inlineForm.querySelector('input[name="selling_plan"]');
      const quantityInput = this.inlineForm.querySelector('input[name="quantity"]');
      
      if (variantInput) {
        variantInput.value = options.product_id;
      }
      
      // Handle selling plan input dynamically
      if (options.selling_plan_id && options.selling_plan_id !== 'null' && options.selling_plan_id !== '') {
        // Selling plan exists - ensure input exists and update it
        if (!sellingPlanInput) {
          // Create the selling plan input if it doesn't exist
          sellingPlanInput = document.createElement('input');
          sellingPlanInput.type = 'hidden';
          sellingPlanInput.name = 'selling_plan';
          // Insert it after the variant input
          if (variantInput && variantInput.nextSibling) {
            variantInput.parentNode.insertBefore(sellingPlanInput, variantInput.nextSibling);
          } else if (variantInput) {
            variantInput.parentNode.appendChild(sellingPlanInput);
          }
        }
        sellingPlanInput.value = options.selling_plan_id;
      } else {
        // No selling plan - remove the input if it exists
        if (sellingPlanInput) {
          sellingPlanInput.remove();
        }
      }
      
      // Ensure quantity is always 1
      if (quantityInput) {
        quantityInput.value = '1';
      }
      
      // Update submit button text with new price
      const submitButton = this.inlineForm.querySelector('.product-form-inline-cart-submit');
      if (submitButton) {
        const buttonText = submitButton.querySelector('span');
        if (buttonText && options.available !== false) {
          // Format price - remove currency symbol and decimal places for .00
          let formattedPrice = options.total_price;
          if (formattedPrice) {
            // Remove .00 or .01 from the price if present
            formattedPrice = formattedPrice.replace(/\.00$|\.01$/, '');
            // buttonText.textContent = `Buy now | ${formattedPrice}`;
            buttonText.textContent = `Buy now ${formattedPrice}`;
          } else {
            buttonText.textContent = submitButton.dataset.addToCartText || 'Add to cart';
          }
          submitButton.disabled = false;
        } else if (buttonText && options.available === false) {
          buttonText.textContent = submitButton.dataset.soldOutText || 'Sold out';
          submitButton.disabled = true;
        }
      }
    }
  }
  
  initializeDefaultPrice() {
    // Set initial price based on first selected tab on page load
    const selectedTab = this.productTabs.find(tab => tab.classList.contains('selected'));
    if (selectedTab) {
      const options = JSON.parse(selectedTab.dataset.options || '{}');
      this.updateProductCardPrice(options);
    } else {
      // Fallback: use first tab if no selected tab
      if (this.productTabs.length > 0) {
        const firstTab = this.productTabs[0];
        const options = JSON.parse(firstTab.dataset.options || '{}');
        this.updateProductCardPrice(options);
      }
    }
  }
  
  updateProductCardPrice(options) {
    if (!this.priceContainer) {
      return;
    }
    
    // Try to get price from any available field
    let currentPrice = null;
    let currentPriceSource = '';
    
    if (options.price) {
      currentPrice = parseFloat(options.price);
      currentPriceSource = 'price';
    } else if (options.price_per_month) {
      currentPrice = parseFloat(options.price_per_month);
      currentPriceSource = 'price_per_month';
    } else if (options.variant_price) {
      currentPrice = parseFloat(options.variant_price);
      currentPriceSource = 'variant_price';
    } else if (options.total_price) {
      // Parse total_price if it's a formatted string like "$29.95"
      if (typeof options.total_price === 'string') {
        const priceMatch = options.total_price.match(/[\d.]+/);
        if (priceMatch) {
          currentPrice = parseFloat(priceMatch[0]);
          currentPriceSource = 'total_price (parsed)';
        }
      } else {
        currentPrice = parseFloat(options.total_price);
        currentPriceSource = 'total_price';
      }
    }
    
    if (!currentPrice) {
      return;
    }
    
    // If price seems to be in cents (> 100), convert to dollars
    if (currentPrice > 100) {
      currentPrice = currentPrice / 100;
    }
    
    // Try to get compare-at price from any available field
    let compareAtPrice = null;
    let compareAtPriceSource = '';
    
    if (options.compare_at_price_per_month) {
      // Parse compare_at_price_per_month if it's a formatted string like "$29.95"
      if (typeof options.compare_at_price_per_month === 'string') {
        const priceMatch = options.compare_at_price_per_month.match(/[\d.]+/);
        if (priceMatch) {
          compareAtPrice = parseFloat(priceMatch[0]);
          compareAtPriceSource = 'compare_at_price_per_month (parsed)';
        }
      } else {
        compareAtPrice = parseFloat(options.compare_at_price_per_month);
        compareAtPriceSource = 'compare_at_price_per_month';
      }
    } else if (options.compare_at_price) {
      compareAtPrice = parseFloat(options.compare_at_price);
      compareAtPriceSource = 'compare_at_price';
    } else if (options.price_strikethrough) {
      // Parse price_strikethrough if it's a formatted string like "$29.95"
      if (typeof options.price_strikethrough === 'string') {
        const priceMatch = options.price_strikethrough.match(/[\d.]+/);
        if (priceMatch) {
          compareAtPrice = parseFloat(priceMatch[0]);
          compareAtPriceSource = 'price_strikethrough (parsed)';
        }
      } else {
        compareAtPrice = parseFloat(options.price_strikethrough);
        compareAtPriceSource = 'price_strikethrough';
      }
    }
    
    if (compareAtPrice) {
      // If price seems to be in cents (> 100), convert to dollars
      if (compareAtPrice > 100) {
        compareAtPrice = compareAtPrice / 100;
      }
    }
    
    // Format prices without trailing zeros
    const formattedPrice = this.formatPrice(currentPrice);
    const formattedCompareAtPrice = compareAtPrice ? this.formatPrice(compareAtPrice) : null;
        
    // Check if this is a sale (compare_at_price exists and is higher than current price)
    const isOnSale = compareAtPrice && compareAtPrice > currentPrice;
    
    if (isOnSale) {
      // Show sale price structure
      if (this.regularPriceContainer) {
        this.regularPriceContainer.style.display = 'none';
      }
      
      if (this.salePriceContainer) {
        this.salePriceContainer.style.display = 'block';
        
        // Update compare-at (strikethrough) price - it's the <s> tag with .price-item--regular
        const compareAtSpan = this.salePriceContainer.querySelector('s .price-item--regular, .price-item--regular');
        if (compareAtSpan) {
          compareAtSpan.textContent = formattedCompareAtPrice;
        }
        
        // Update sale price - it's the .price-item--sale span
        const salePriceSpan = this.salePriceContainer.querySelector('.price-item--sale');
        if (salePriceSpan) {
          salePriceSpan.textContent = formattedPrice;
        }
      }
    } else {
      // Show regular price structure
      if (this.salePriceContainer) {
        this.salePriceContainer.style.display = 'none';
      }
      
      if (this.regularPriceContainer) {
        this.regularPriceContainer.style.display = 'block';
        
        // Update regular price - it's the .price-item--regular in the .price__regular container
        const regularPriceSpan = this.regularPriceContainer.querySelector('.price-item--regular');
        if (regularPriceSpan) {
          regularPriceSpan.textContent = formattedPrice;
        }
      } else {
        // Fallback: try to update any price element we can find
        const anyPriceElement = this.priceContainer.querySelector('.price-item, .money, [data-price]');
        if (anyPriceElement) {
          anyPriceElement.textContent = formattedPrice;
        }
      }
    }
  }
  
  formatPrice(priceInDollars) {
    // Format price similar to Shopify's money_without_trailing_zeros filter
    return '$' + priceInDollars.toFixed(2).replace(/\.00$/, '');
  }
  
  closeDropdown() {
    this.dropdown.classList.remove('is-open');
    this.optionsContainer.style.display = 'none';
    // Reset positioning class when closing
    this.dropdown.classList.remove('dropdown-up');
  }
  
  adjustDropdownPosition() {
    // Check if dropdown should open upward based on card position
    if (this.isInLastRow()) {
      // Add class to position dropdown above the trigger
      this.dropdown.classList.add('dropdown-up');
    } else {
      // Remove class for default downward positioning
      this.dropdown.classList.remove('dropdown-up');
    }
  }
  
  isInLastRow() {
    // Find the grid container
    const gridContainer = this.productCard.closest('.product-grid');
    if (!gridContainer) return false;
    
    // Get all product cards in the grid
    const allCards = Array.from(gridContainer.querySelectorAll('[data-product-card]'));
    const currentCardIndex = allCards.indexOf(this.productCard);
    
    if (currentCardIndex === -1) return false;
    
    // Get computed grid style to determine number of columns
    const gridStyle = window.getComputedStyle(gridContainer);
    const gridTemplateColumns = gridStyle.gridTemplateColumns;
    
    // Count the number of columns by counting the number of column definitions
    const columnCount = gridTemplateColumns.split(' ').length;
    
    // Calculate total rows and current row
    const totalCards = allCards.length;
    const totalRows = Math.ceil(totalCards / columnCount);
    const currentRow = Math.ceil((currentCardIndex + 1) / columnCount);
    
    // Check if current card is in the last row
    return currentRow === totalRows;
  }
  
  handleResize() {
    // If dropdown is open, recalculate position after resize
    if (this.dropdown && this.dropdown.classList.contains('is-open')) {
      this.adjustDropdownPosition();
    }
  }
  
  destroy() {
    // Clean up event listener when instance is destroyed
    window.removeEventListener('resize', this.handleResize);
  }
  
  closeOtherDropdowns() {
    // Close all other dropdowns on the page
    const otherDropdowns = document.querySelectorAll('.product-form__input--dropdown.is-open');
    otherDropdowns.forEach((dropdown) => {
      if (dropdown !== this.dropdown) {
        dropdown.classList.remove('is-open');
        const optionsContainer = dropdown.querySelector('.product-form-inline_dropdown__options');
        if (optionsContainer) {
          optionsContainer.style.display = 'none';
        }
      }
    });
  }
}

// Global dropdown manager
class ProductCardDropdownManager {
  constructor() {
    this.instances = new Map();
    this.init();
  }
  
  init() {
    // Initialize dropdowns on page load
    this.initializeDropdowns();
    
    // Handle clicks outside dropdowns
    document.addEventListener('click', (e) => {
      if (!e.target.closest('.product-form__input--dropdown')) {
        this.closeAllDropdowns();
      }
    });
    
    // Handle escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.closeAllDropdowns();
      }
    });
    
    // Watch for new product cards added dynamically
    this.observeNewCards();
  }
  
  initializeDropdowns() {
    const productCards = document.querySelectorAll('[data-product-card]');
    productCards.forEach((card) => {
      const cardId = card.dataset.productCard;
      if (!this.instances.has(cardId)) {
        const dropdown = new ProductCardDropdown(card);
        this.instances.set(cardId, dropdown);
      }
    });
  }
  
  closeAllDropdowns() {
    this.instances.forEach((instance) => {
      if (instance.dropdown && instance.dropdown.classList.contains('is-open')) {
        instance.closeDropdown();
      }
    });
  }
  
  observeNewCards() {
    // Use MutationObserver to watch for dynamically added product cards
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) { // Element node
            // Check if the added node is a product card
            if (node.hasAttribute && node.hasAttribute('data-product-card')) {
              const cardId = node.dataset.productCard;
              if (!this.instances.has(cardId)) {
                const dropdown = new ProductCardDropdown(node);
                this.instances.set(cardId, dropdown);
              }
            }
            // Check if the added node contains product cards
            const productCards = node.querySelectorAll ? node.querySelectorAll('[data-product-card]') : [];
            productCards.forEach((card) => {
              const cardId = card.dataset.productCard;
              if (!this.instances.has(cardId)) {
                const dropdown = new ProductCardDropdown(card);
                this.instances.set(cardId, dropdown);
              }
            });
          }
        });
      });
    });
    
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  window.productCardDropdownManager = new ProductCardDropdownManager();
});
